/**
 * Discount codes and access codes — the academy's own screen.
 *
 * Until this existed the only way to create a campaign was a REST call by hand, and the only way
 * to switch the feature on was a shell. This is the product surface for the first half; the
 * platform console owns the switch.
 *
 * THE DESIGN IS STRIPE-NATIVE AND THIS SCREEN DOES NOT FIGHT IT
 * ------------------------------------------------------------
 * Four refusals here are not missing features. Each one is a Stripe limitation that a screen
 * could only ever paper over, and papering over it would mean promising something Stripe will
 * refuse — which is worse than not offering it:
 *
 *   NO EDIT of the economics. Stripe fixes a Coupon's `percent_off`/`amount_off`, its
 *   `applies_to`, its `max_redemptions`, its `expires_at` and its `restrictions[minimum_amount]`
 *   when the Coupon is created, and offers no call that changes any of them at any API version.
 *   Only `active` and `metadata` are modifiable. So there is no edit form: it would save the
 *   name and silently discard the cap beside it. There is a REISSUE, and a new campaign.
 *
 *   NO REACTIVATION of an exhausted code, and a WARNING BEFORE MINTING one with a cap. A
 *   Promotion Code that reaches `max_redemptions` or `expires_at` is permanently inactive.
 *   `max_total_redemptions` is therefore a one-way door and the form says so before it is used,
 *   in the server's own sentence — a number in a box does not communicate "permanent".
 *
 *   NO DISCOUNT-ENTRY FIELD, here or anywhere in this app, and NO validate call. Learners type
 *   a code into STRIPE's field on STRIPE's page. Two promo fields would let one learner apply a
 *   thinlms code and a Stripe code and receive a reduction nobody priced, and Stripe accepts at
 *   most one discount per Session anyway. Nothing on this screen claims a code is valid: that
 *   is Stripe's judgement and this app cannot make it.
 *
 *   NO 100%-OFF DISCOUNT. `percent_off: 100` is refused at the mint-time guard rail, because a
 *   zero-total Session is adjudicated inside Stripe where this app cannot intercept it. The
 *   form refuses it locally with the same redirect the server gives — an access code — and the
 *   server refuses it again regardless, because a client-side guard is not the boundary.
 *
 * AND ONE HONESTY RULE THAT IS NOT A REFUSAL. A per-learner limit on a SHARED code is
 * ADVISORY: Stripe has no per-customer cap, so an over-use is flagged after the fact and the
 * learner keeps what they bought. The only construction where it is enforced is a code assigned
 * to one email address, which pins it to that learner's Stripe customer. The form carries the
 * server's own sentence for whichever of the two the current input produces.
 *
 * MONEY IS INTEGER MINOR UNITS. The two amount boxes hold major units because that is what an
 * operator types, and each is rounded to an integer ONCE, against the currency's own digit
 * count, before anything is sent. Every amount DISPLAYED goes through `formatMoney`.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Icon,
  SelectField,
  Spinner,
  type Column,
} from '../../shared/ui';
import { formatMoney, minorUnitDigits } from '../../shared/lib/pricing';
import { PageHeader } from '../AdminShell';
import {
  APPLIES_COURSES,
  APPLIES_PATHWAYS,
  APPLIES_SPECIFIC,
  campaignScope,
  campaignScopeSummary,
  campaignState,
  campaignsKey,
  capOf,
  createCampaign,
  deactivateCampaign,
  DISCOUNT_FIXED,
  DISCOUNT_PERCENT,
  explainPromotionFailure,
  KIND_ACCESS,
  KIND_STRIPE,
  loadCampaigns,
  loadPromotionSettings,
  loadRedemptions,
  loadScopeOptions,
  phaseOf,
  previewCampaign,
  PROMO_COPY,
  PROMOTIONS_KEY,
  promotionSettingsKey,
  redemptionsKey,
  reissueCode,
  scopeOptionList,
  scopeOptionsKey,
  scopeOptionsState,
  type CampaignDraft,
  type PromoKind,
  type WireCampaign,
  type WireCampaignPreview,
  type WireScopeOption,
} from '../api/promotions';

/* --------------------------------------------------------------- shared pieces ---- */

/** The server's sentence, plus the next step for the codes whose next step is not a retry. */
function Refusal({ error }: { error: unknown }) {
  const failure = explainPromotionFailure(error);
  return (
    <Banner tone="warning">
      <p>{failure.message}</p>
      {failure.nextStep && <p>{failure.nextStep}</p>}
    </Banner>
  );
}

function StateBadge({ row }: { row: WireCampaign }) {
  const state = campaignState(row);
  switch (state.kind) {
    case 'still_live_in_stripe':
      return <Badge tone="danger">Off here, live in Stripe</Badge>;
    case 'mint_failed':
      return <Badge tone="danger">Never reached Stripe</Badge>;
    case 'drifted':
      return <Badge tone="warning">Disagrees with Stripe</Badge>;
    case 'minting':
      return <Badge tone="warning">Being created</Badge>;
    case 'inactive':
      return <Badge tone="neutral">Switched off</Badge>;
    case 'scheduled':
      return <Badge tone="warning">Starts later</Badge>;
    default:
      return <Badge tone="success">Live</Badge>;
  }
}

/** The discount, as one string, never re-derived. `percent_off` stays a string end to end. */
function discountLabel(row: WireCampaign): string {
  if (row.kind === KIND_ACCESS) return 'Free — the whole item';
  if (row.discount_type === DISCOUNT_PERCENT && row.percent_off) return `${row.percent_off}% off`;
  if (row.discount_type === DISCOUNT_FIXED && typeof row.amount_off_minor === 'number') {
    return `${formatMoney(row.amount_off_minor, row.currency || 'MYR')} off`;
  }
  // Neither set. Absent, not zero — a campaign mid-creation, or one whose kind we do not know.
  return 'Not stated';
}

/* ---------------------------------------------------------------------- rows ---- */

function columns(): Column<WireCampaign>[] {
  return [
    {
      key: 'code',
      header: 'Code',
      mono: true,
      render: (row) => (
        <span>
          {row.code}
          <br />
          <small className="admin-muted">{row.name || 'no name given'}</small>
        </span>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      render: (row) =>
        row.kind === KIND_ACCESS ? (
          <Badge tone="neutral">Access code</Badge>
        ) : (
          <Badge tone="neutral">Discount code</Badge>
        ),
    },
    { key: 'discount', header: 'What it does', render: (row) => discountLabel(row) },
    {
      key: 'scope',
      header: 'Covers',
      // NAMES, not a count over a database enum. An admin scanning this list is asking "which
      // code is the one for the welding course", and `3 items / specific_items` cannot answer
      // it. The full list is in the detail panel.
      render: (row) => <span>{campaignScopeSummary(row)}</span>,
    },
    {
      key: 'cap',
      header: 'Limit',
      render: (row) => {
        const cap = capOf(row);
        return cap.kind === 'capped' ? (
          // NOT a remaining count: this screen does not hold Stripe's `times_redeemed`, and
          // subtracting our mirror from the cap would print a number Stripe disagrees with.
          <span>{cap.limit} in total</span>
        ) : (
          <span className="admin-muted">No limit set</span>
        );
      },
    },
    { key: 'state', header: 'State', render: (row) => <StateBadge row={row} /> },
  ];
}

/* -------------------------------------------------------------------- details ---- */

/**
 * One campaign in full, with the two things a row cannot hold: the enforcement sentence, and
 * the redemption counts.
 *
 * THE COUNTS ARE FETCHED ON DEMAND, one campaign at a time, and never for every row: the
 * redemptions route reads Stripe's `times_redeemed` for the campaign it is asked about, so a
 * column of them would be one Stripe call per row on every page load.
 *
 * OURS AND STRIPE'S ARE SHOWN SIDE BY SIDE AND NEVER RECONCILED INTO ONE NUMBER. One number
 * hides both a webhook lag and a real drift, and telling those apart is the entire point of
 * showing both. A count Stripe would not give is UNKNOWN, never zero.
 */
function CampaignDetail({ row, onClose }: { row: WireCampaign; onClose: () => void }) {
  const queryClient = useQueryClient();
  const redemptions = useQuery({
    queryKey: redemptionsKey(row.id),
    queryFn: () => loadRedemptions(row.id),
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: PROMOTIONS_KEY });

  const deactivate = useMutation({
    mutationFn: () => deactivateCampaign(row.id),
    onSuccess: invalidate,
  });
  const reissue = useMutation({ mutationFn: () => reissueCode(row.id), onSuccess: invalidate });

  const state = campaignState(row);

  return (
    <Card
      title={`${row.code}`}
      action={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {state.kind === 'still_live_in_stripe' && (
        <Banner tone="danger">
          <p>{state.sentence}</p>
        </Banner>
      )}
      {state.kind === 'mint_failed' && (
        <Banner tone="danger">
          <p>
            <strong>This code was never created in Stripe.</strong> {state.message}
          </p>
          <p>
            Nothing about it works at checkout. Its economics cannot be repaired in place —
            create a new campaign.
          </p>
        </Banner>
      )}
      {state.kind === 'drifted' && (
        <Banner tone="warning">
          <p>
            <strong>Stripe and this record disagree about this code.</strong> Stripe is the
            authority on whether it works; this screen is not.
          </p>
        </Banner>
      )}

      <dl className="people__facts">
        <div>
          <dt>What it does</dt>
          <dd>{discountLabel(row)}</dd>
        </div>
        <div>
          <dt>Covers</dt>
          <dd>
            <CampaignScopeList row={row} />
            <small className="admin-muted">{PROMO_COPY.scopeIsFixed}</small>
          </dd>
        </div>
        <div>
          <dt>Per learner</dt>
          {/* THE SERVER'S OWN SENTENCE. It says either "Stripe itself refuses the second use"
              or "checked afterwards and flagged, not blocked", and the difference is the most
              expensive thing on this screen to get wrong. */}
          <dd>{row.per_learner_enforcement}</dd>
        </div>
        <div>
          <dt>Minimum spend</dt>
          <dd>
            {typeof row.min_spend_minor === 'number'
              ? formatMoney(row.min_spend_minor, row.currency || 'MYR')
              : 'None set'}
          </dd>
        </div>
        <div>
          <dt>Valid</dt>
          <dd>
            {row.valid_from ? new Date(row.valid_from).toLocaleString() : 'From creation'} —{' '}
            {row.valid_until ? new Date(row.valid_until).toLocaleString() : 'no end date'}
          </dd>
        </div>
        <div>
          <dt>Created by</dt>
          <dd>
            {row.created_by_username || 'not recorded'} on{' '}
            {new Date(row.created_at).toLocaleDateString()}
          </dd>
        </div>
        {row.superseded_codes.length > 0 && (
          <div>
            <dt>Replaced codes</dt>
            <dd className="mono">{row.superseded_codes.join(', ')}</dd>
          </div>
        )}
      </dl>

      <h4>Redemptions</h4>
      {redemptions.isPending ? (
        <Spinner label="Reading this code’s redemptions…" />
      ) : redemptions.isError ? (
        <Refusal error={redemptions.error} />
      ) : (
        <>
          <ul className="ops-results">
            {Object.entries(redemptions.data?.summary ?? {}).map(([key, value]) => (
              <li key={key}>
                {key.replaceAll('_', ' ')}:{' '}
                {/* `null` FROM THE SERVER MEANS UNKNOWN. Stripe's count is null when Stripe
                    could not be read, and a missing number and a zero look identical in a list
                    and mean opposite things. */}
                {value === null || value === undefined ? (
                  <em>unknown — this could not be read</em>
                ) : (
                  String(value)
                )}
              </li>
            ))}
          </ul>
          {(redemptions.data?.results.length ?? 0) === 0 && (
            <p className="admin-muted">Nobody has used this code yet.</p>
          )}
          {redemptions.data?.results.some((entry) => entry.over_limit) && (
            <Banner tone="warning">
              <p>
                <strong>Somebody has used this code more times than its per-learner limit.</strong>{' '}
                It was not blocked, because Stripe has no per-customer limit for a shared code.
              </p>
              <ul>
                {(redemptions.data?.remedies_for_over_limit ?? []).map((remedy) => (
                  <li key={remedy}>{remedy}</li>
                ))}
              </ul>
            </Banner>
          )}
        </>
      )}

      <h4>What can be changed</h4>
      <p className="admin-muted">
        {PROMO_COPY.noEditsOffered} {PROMO_COPY.economicsAreFrozen}
      </p>
      <p className="admin-muted">{PROMO_COPY.reissueExplained}</p>

      {deactivate.isError && <Refusal error={deactivate.error} />}
      {reissue.isError && <Refusal error={reissue.error} />}

      <div className="console__form-actions">
        {row.is_active && (
          <Button variant="danger" busy={deactivate.isPending} onClick={() => deactivate.mutate()}>
            <Icon name="block" />
            Switch this code off
          </Button>
        )}
        {row.kind === KIND_STRIPE && (
          <Button busy={reissue.isPending} onClick={() => reissue.mutate()}>
            <Icon name="autorenew" />
            Reissue the code
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * What an existing code covers, in words rather than in keys.
 *
 * The scope used to print as a comma-joined list of `course-v1:` strings, which is the same
 * information an admin could not read. The titles are the ones recorded WHEN THE CODE WAS
 * CREATED, and that is the right choice rather than the convenient one: a course renamed since
 * is still the course this coupon names, and printing today's title would describe something
 * the code was not created against. It is also the only way a since-deleted item stays
 * legible at all.
 *
 * An older server sends no `applies_to_labels` field. That is ABSENT, not empty, and it is
 * rendered as the keys it does send with a sentence saying they are keys — a screen that read
 * a missing field as "this code names nothing" would hide a scope it can still show.
 */
function CampaignScopeList({ row }: { row: WireCampaign }) {
  const scope = campaignScope(row);

  if (scope.kind === 'named') {
    return (
      <ul className="ops-results">
        {scope.items.map((item) => (
          <li key={item.key}>
            {item.title}{' '}
            <small className="admin-muted">
              ({item.itemKind === 'pathway' ? 'learning path' : 'course'}) <code>{item.key}</code>
            </small>
          </li>
        ))}
      </ul>
    );
  }

  if (scope.kind === 'keys') {
    return (
      <>
        <ul className="ops-results">
          {scope.keys.map((key) => (
            <li key={key}>
              <code>{key}</code>
            </li>
          ))}
        </ul>
        <small className="admin-muted">
          This academy did not send names for these, so they are shown as the keys the code was
          created against.
        </small>
        <br />
      </>
    );
  }

  return (
    <>
      {scope.appliesTo === APPLIES_PATHWAYS
        ? 'Every learning path that was priced when this code was created'
        : 'Every course that was priced when this code was created'}{' '}
      — {scope.count} of them.
      <br />
    </>
  );
}

/* --------------------------------------------------------------------- create ---- */

interface DraftForm {
  kind: PromoKind;
  name: string;
  code: string;
  discountType: string;
  percentOff: string;
  amountOffMajor: string;
  currency: string;
  appliesTo: string;
  /**
   * The item keys this code will name, as a LIST.
   *
   * A list rather than the pasted string it used to be, because the picker and the paste box
   * are two ways to fill ONE value and a screen that kept both as separate state would have to
   * decide which one wins — a decision it would get wrong on the day both were touched.
   */
  items: string[];
  validUntil: string;
  maxTotal: string;
  maxPerLearner: string;
  minSpendMajor: string;
  assignedEmail: string;
}

const EMPTY: DraftForm = {
  kind: KIND_STRIPE,
  name: '',
  code: '',
  discountType: DISCOUNT_PERCENT,
  percentOff: '',
  amountOffMajor: '',
  currency: 'MYR',
  appliesTo: APPLIES_COURSES,
  items: [],
  validUntil: '',
  maxTotal: '',
  maxPerLearner: '1',
  minSpendMajor: '',
  assignedEmail: '',
};

/**
 * Major units to an integer number of minor units, rounded ONCE, against this currency's own
 * digit count.
 *
 * `NaN` for anything unparseable, which the caller reads as "not given" rather than as zero —
 * an amount-off of zero is a campaign that discounts nothing and the server would refuse it,
 * but a blank box must not become one silently.
 */
function minorFrom(major: string, currency: string): number {
  const parsed = Number.parseFloat(major.replace(/,/g, ''));
  if (!Number.isFinite(parsed)) return Number.NaN;
  return Math.round(parsed * 10 ** minorUnitDigits(currency || 'MYR'));
}

/** A positive integer, or null when the box is empty. Never 0 from a blank field. */
function countFrom(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function toDraft(form: DraftForm): CampaignDraft {
  const stripe = form.kind === KIND_STRIPE;
  const amountOff = minorFrom(form.amountOffMajor, form.currency);
  const minSpend = minorFrom(form.minSpendMajor, form.currency);
  const maxTotal = countFrom(form.maxTotal);
  const perLearner = countFrom(form.maxPerLearner);

  return {
    kind: form.kind,
    name: form.name,
    // Blank means "generate one". Sent as an absent field rather than as an empty string so
    // the server's own generator runs instead of validating a blank code.
    ...(form.code.trim() ? { code: form.code.trim() } : {}),
    ...(form.appliesTo === APPLIES_SPECIFIC
      ? { applies_to: APPLIES_SPECIFIC, applies_to_items: form.items }
      : { applies_to: form.appliesTo }),
    // An ACCESS CODE HAS NO DISCOUNT FIELDS AT ALL. A check constraint forbids these columns on
    // one, so sending them would surface as a constraint violation in Django's wording instead
    // of the sentence that says an access code grants the whole item.
    ...(stripe
      ? {
          discount_type: form.discountType,
          currency: form.currency,
          ...(form.discountType === DISCOUNT_PERCENT
            ? // A STRING, so no float reaches a Decimal column.
              { percent_off: form.percentOff.trim() }
            : Number.isFinite(amountOff)
              ? { amount_off_minor: amountOff }
              : {}),
          ...(Number.isFinite(minSpend) ? { min_spend_minor: minSpend } : {}),
        }
      : {}),
    ...(form.validUntil ? { valid_until: new Date(form.validUntil).toISOString() } : {}),
    ...(maxTotal !== null ? { max_total_redemptions: maxTotal } : {}),
    ...(perLearner !== null ? { max_per_learner: perLearner } : {}),
    ...(form.assignedEmail.trim() ? { assigned_email: form.assignedEmail.trim() } : {}),
  };
}

/**
 * The one refusal this form makes locally, and it makes it for a reason.
 *
 * Everything else is left to the server: a client-side guard that disagrees with the server is
 * a lie in whichever direction it errs, and every other guard here needs the academy's prices,
 * which this screen does not hold. `percent_off >= 100` needs nothing but the number, the
 * server's refusal for it is a REDIRECT to a different feature rather than a correction, and
 * arriving at that redirect after a failed mint is a worse way to learn it. The server refuses
 * it again regardless — this is not the boundary.
 */
function hundredPercent(form: DraftForm): boolean {
  if (form.kind !== KIND_STRIPE || form.discountType !== DISCOUNT_PERCENT) return false;
  const parsed = Number.parseFloat(form.percentOff);
  return Number.isFinite(parsed) && parsed >= 100;
}

/* ---------------------------------------------------------------- scope picker ---- */

/**
 * The items this code will name, chosen by NAME instead of typed as a key.
 *
 * Before this, scoping a code to one course meant pasting `course-v1:ORG+NUM+RUN` into a text
 * box, and no admin knows those. The list is the same catalogue the mint-time guard reads —
 * priced now, synced to Stripe now — because those are the only items that have a Stripe
 * Product for `applies_to` to name, and offering anything else would let an admin build a
 * scope Stripe refuses.
 *
 * THE FOUR ANSWERS ARE FOUR ANSWERS. A deployment that cannot read the catalogue, an academy
 * that has priced nothing, a read that failed for some other reason, and a real list are
 * rendered differently and say different things. Only the first offers the paste box, and it
 * offers it as a way through rather than as the normal path: the server resolves and guards
 * the scope again before it creates anything, so a key typed here is checked, not trusted.
 */
function ScopePicker({
  picked,
  onChange,
}: {
  picked: string[];
  onChange: (keys: string[]) => void;
}) {
  const query = useQuery({
    queryKey: scopeOptionsKey(),
    queryFn: loadScopeOptions,
    retry: false,
  });
  const [search, setSearch] = useState('');
  const state = scopeOptionsState(query);

  const chosen = new Set(picked);
  const toggle = (key: string) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...next]);
  };

  if (state.kind === 'loading') return <Spinner label="Reading this academy’s priced items…" />;

  if (state.kind === 'unwired' || state.kind === 'failed') {
    return (
      <>
        <Banner tone="warning">
          <p>
            <strong>The list of items could not be read.</strong> {state.failure.message}
          </p>
          <p>{PROMO_COPY.scopeCatalogueUnknown}</p>
        </Banner>
        <Field
          label="Item keys"
          name="promo-items"
          hint="Course or path keys, separated by spaces or commas."
          value={picked.join(' ')}
          onChange={(event) =>
            onChange(
              event.target.value
                .split(/[\s,]+/)
                .map((item) => item.trim())
                .filter(Boolean),
            )
          }
        />
      </>
    );
  }

  if (state.kind === 'empty') {
    return (
      <Banner tone="warning">
        <p>{PROMO_COPY.scopeEmpty}</p>
      </Banner>
    );
  }

  const all = scopeOptionList(state.options);
  const needle = search.trim().toLowerCase();
  // Filtering hides rows; it never unpicks them. An admin who searches after choosing three
  // courses must not silently lose the two the search does not match, so the count below is
  // of what is CHOSEN and not of what is visible.
  const visible = needle
    ? all.filter(
        (option) =>
          option.title.toLowerCase().includes(needle) ||
          option.item_key.toLowerCase().includes(needle),
      )
    : all;

  const row = (option: WireScopeOption) => (
    <li key={option.item_key}>
      <label className="ops-choice">
        <input
          type="checkbox"
          checked={chosen.has(option.item_key)}
          onChange={() => toggle(option.item_key)}
        />
        <span>
          {/* The server's own title, and the key beneath it — the key is what is SENT, and an
              admin who has to tell two similarly named courses apart needs to see it. */}
          <strong>{option.title || option.item_key}</strong>
          <br />
          <small className="admin-muted">
            {option.item_kind === 'pathway' ? 'Learning path' : 'Course'} ·{' '}
            {formatMoney(option.amount_minor, option.currency)} · <code>{option.item_key}</code>
          </small>
        </span>
      </label>
    </li>
  );

  return (
    <>
      <p className="admin-muted">{PROMO_COPY.scopeIsProductsOnly}</p>
      <Field
        label="Search"
        name="promo-scope-search"
        hint="Filters the list below. It never changes what is already chosen."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <p>
        <Badge tone={picked.length > 0 ? 'success' : 'warning'}>
          {picked.length} chosen of {all.length} priced and synced
        </Badge>
      </p>
      {visible.length === 0 ? (
        <p className="admin-muted">Nothing matches “{search}”.</p>
      ) : (
        <ul className="ops-results">{visible.map(row)}</ul>
      )}
      {/* A key that is chosen but is NOT in the catalogue any more. It is still part of the
          scope being built, so it is shown rather than dropped: silently discarding it is how
          a code gets created covering less than the admin asked for. */}
      {picked
        .filter((key) => !all.some((option) => option.item_key === key))
        .map((key) => (
          <p key={key} className="admin-muted">
            <code>{key}</code> is chosen but is not priced and synced right now, so it has no
            Stripe Product to name. The academy checks the scope again before creating the code.
          </p>
        ))}
    </>
  );
}

function CreateCampaign({
  canMintDiscount,
  canMintAccess,
  onCreated,
}: {
  canMintDiscount: boolean;
  canMintAccess: boolean;
  onCreated: (row: WireCampaign) => void;
}) {
  const [form, setForm] = useState<DraftForm>(() => ({
    ...EMPTY,
    kind: canMintDiscount ? KIND_STRIPE : KIND_ACCESS,
  }));
  const [capAcknowledged, setCapAcknowledged] = useState(false);
  const [preview, setPreview] = useState<WireCampaignPreview | null>(null);

  const set = <K extends keyof DraftForm>(key: K, value: DraftForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    // ANY change invalidates the preview. A preview drawn for a 20% code must not still be on
    // screen, saying "guard passed", beside a form that now says 100%.
    setPreview(null);
  };

  const stripe = form.kind === KIND_STRIPE;
  const capped = countFrom(form.maxTotal) !== null;
  const pinned = Boolean(form.assignedEmail.trim());
  const refusedLocally = hundredPercent(form);

  const previewCall = useMutation({
    mutationFn: () => previewCampaign(toDraft(form)),
    onSuccess: setPreview,
  });
  const create = useMutation({
    mutationFn: () => createCampaign(toDraft(form)),
    onSuccess: (row) => {
      setForm({ ...EMPTY, kind: form.kind });
      setCapAcknowledged(false);
      setPreview(null);
      onCreated(row);
    },
  });

  const kindOptions: [string, string][] = [
    ...(canMintDiscount ? ([[KIND_STRIPE, 'Discount code (Stripe applies it at checkout)']] as [string, string][]) : []),
    ...(canMintAccess ? ([[KIND_ACCESS, 'Access code (grants the item free, no Stripe call)']] as [string, string][]) : []),
  ];

  // The cap is a ONE-WAY DOOR, so the create button is withheld until the operator has said
  // they understand that. Not a styling choice: Stripe cannot raise, lower or remove it, and a
  // code that reaches it is permanently dead.
  const blocked =
    refusedLocally ||
    !form.name.trim() ||
    (capped && !capAcknowledged) ||
    (stripe && (preview === null || !preview.guard_ok));

  return (
    <Card title="Create a code">
      <SelectField
        label="Kind"
        name="promo-kind"
        value={form.kind}
        onChange={(event) => set('kind', event.target.value as PromoKind)}
        options={kindOptions}
      />

      <Field
        label="Name"
        name="promo-name"
        hint="For your own records. Learners never see it."
        value={form.name}
        onChange={(event) => set('name', event.target.value)}
      />
      <Field
        label="Code"
        name="promo-code"
        hint="Leave blank and one will be generated. Learners type this into Stripe’s own field on the checkout page."
        value={form.code}
        onChange={(event) => set('code', event.target.value)}
      />

      {stripe ? (
        <>
          <SelectField
            label="Reduction"
            name="promo-discount-type"
            value={form.discountType}
            onChange={(event) => set('discountType', event.target.value)}
            options={[
              [DISCOUNT_PERCENT, 'A percentage off'],
              [DISCOUNT_FIXED, 'A fixed amount off'],
            ]}
          />
          {form.discountType === DISCOUNT_PERCENT ? (
            <Field
              label="Percent off"
              name="promo-percent"
              inputMode="decimal"
              hint="Less than 100. A 100%-off code is an access code, not a Stripe coupon."
              value={form.percentOff}
              onChange={(event) => set('percentOff', event.target.value)}
              error={refusedLocally ? PROMO_COPY.hundredPercentIsAnAccessCode : undefined}
            />
          ) : (
            <Field
              label={`Amount off (${form.currency})`}
              name="promo-amount"
              inputMode="decimal"
              hint="Typed as an amount; sent as a whole number of minor units."
              value={form.amountOffMajor}
              onChange={(event) => set('amountOffMajor', event.target.value)}
            />
          )}
          <Field
            label="Currency"
            name="promo-currency"
            hint="A Stripe coupon carrying an amount off applies only to prices in its own currency."
            value={form.currency}
            onChange={(event) => set('currency', event.target.value)}
          />
          <Field
            label={`Minimum spend (${form.currency}, optional)`}
            name="promo-min-spend"
            inputMode="decimal"
            value={form.minSpendMajor}
            onChange={(event) => set('minSpendMajor', event.target.value)}
          />
        </>
      ) : (
        <Banner tone="info">
          <p>
            An access code grants the whole item free. It has no discount to set, makes no Stripe
            API call, and keeps working while this academy&rsquo;s Stripe account is
            disconnected or unreachable.
          </p>
        </Banner>
      )}

      <SelectField
        label="What it covers"
        name="promo-applies-to"
        value={form.appliesTo}
        onChange={(event) => set('appliesTo', event.target.value)}
        options={[
          [APPLIES_COURSES, 'Every priced course in this academy'],
          [APPLIES_PATHWAYS, 'Every priced learning path in this academy'],
          [APPLIES_SPECIFIC, 'Only the courses and paths I choose'],
        ]}
      />
      {form.appliesTo === APPLIES_SPECIFIC && (
        <ScopePicker picked={form.items} onChange={(keys) => set('items', keys)} />
      )}
      <p className="admin-muted">{PROMO_COPY.scopeIsFixed}</p>

      <Field
        label="Last day it works (optional)"
        name="promo-valid-until"
        type="datetime-local"
        hint="Stripe cannot change or clear an expiry once the coupon exists."
        value={form.validUntil}
        onChange={(event) => set('validUntil', event.target.value)}
      />

      <Field
        label="Total uses allowed (optional)"
        name="promo-max-total"
        inputMode="numeric"
        hint="Leave blank for no limit."
        value={form.maxTotal}
        onChange={(event) => set('maxTotal', event.target.value)}
      />
      {capped && (
        <Banner tone="warning">
          <p>
            <strong>{PROMO_COPY.capIsIrreversible}</strong>
          </p>
          {/* The server's own sentence for THIS number, when a preview has been run. */}
          {preview?.irreversible_cap_warning && <p>{preview.irreversible_cap_warning}</p>}
          <label className="ops-choice">
            <input
              type="checkbox"
              checked={capAcknowledged}
              onChange={(event) => setCapAcknowledged(event.target.checked)}
            />
            <span>I understand this limit can never be changed.</span>
          </label>
        </Banner>
      )}

      <Field
        label="Uses allowed per learner"
        name="promo-per-learner"
        inputMode="numeric"
        value={form.maxPerLearner}
        onChange={(event) => set('maxPerLearner', event.target.value)}
      />
      <Field
        label="Assign to one email address (optional)"
        name="promo-assigned-email"
        hint="Pins the code to that learner’s Stripe customer, which is the only way Stripe itself enforces one-per-learner."
        value={form.assignedEmail}
        onChange={(event) => set('assignedEmail', event.target.value)}
      />
      {stripe && !pinned && (
        <Banner tone="info">
          <p>{PROMO_COPY.perLearnerIsAdvisory}</p>
          {/* And the server's own version of the same sentence, once it has been asked. */}
          {preview?.per_learner_enforcement_note && (
            <p>{preview.per_learner_enforcement_note}</p>
          )}
        </Banner>
      )}

      {previewCall.isError && <Refusal error={previewCall.error} />}
      {create.isError && <Refusal error={create.error} />}

      {preview && (
        <>
          <h4>
            What this would cover{' '}
            <Badge tone={preview.guard_ok ? 'success' : 'danger'}>
              {preview.covered_count} of {preview.tenant_priced_count} priced items
            </Badge>
          </h4>
          {preview.covered_count === 0 ? (
            <p className="admin-muted">
              Nothing. A code that covers nothing would never discount anything.
            </p>
          ) : (
            <ul className="ops-results">
              {preview.covered_products.map((key) => (
                <li key={key}>
                  <code>{key}</code>
                </li>
              ))}
            </ul>
          )}
          <p className="admin-muted">{preview.scope_does_not_grow}</p>

          {!preview.guard_ok && (
            <Banner tone="danger">
              <p>
                <strong>This code cannot be created.</strong> {preview.guard_message}
              </p>
            </Banner>
          )}

          {preview.illustration.length > 0 && (
            <>
              <h4>What a learner would pay</h4>
              {/* THE SERVER'S LABEL, VERBATIM AND FIRST. thinlms computes no discount; this is
                  an illustration of Stripe's arithmetic and must never read as a quote. */}
              <p className="admin-muted">{preview.illustration_label}</p>
              <ul className="ops-results">
                {preview.illustration.map((line) => (
                  <li key={line.item_key}>
                    <code>{line.item_key}</code>: {formatMoney(line.price_minor, line.currency)} →{' '}
                    {formatMoney(line.total_after_code_minor, line.currency)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      <div className="console__form-actions">
        {stripe && (
          <Button busy={previewCall.isPending} onClick={() => previewCall.mutate()}>
            <Icon name="visibility" />
            Check what this would do
          </Button>
        )}
        <Button
          variant="primary"
          busy={create.isPending}
          disabled={blocked}
          onClick={() => create.mutate()}
        >
          Create
        </Button>
      </div>
      {stripe && preview === null && (
        <p className="admin-muted">
          A discount code is checked against this academy&rsquo;s real prices before it can be
          created — the checks are the academy&rsquo;s, and they need the prices.
        </p>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------------- page ---- */

export default function AdminPromotions() {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<number | null>(null);
  const [created, setCreated] = useState<WireCampaign | null>(null);

  const settings = useQuery({
    queryKey: promotionSettingsKey(),
    queryFn: loadPromotionSettings,
    retry: false,
  });
  const campaigns = useQuery({
    queryKey: campaignsKey(),
    queryFn: loadCampaigns,
    retry: false,
  });

  const discountPhase = phaseOf(settings.data, KIND_STRIPE);
  const accessPhase = phaseOf(settings.data, KIND_ACCESS);
  const rows = campaigns.data?.results ?? [];
  const selected = useMemo(() => rows.find((row) => row.id === chosen) ?? null, [rows, chosen]);

  return (
    <>
      <PageHeader
        title="Codes"
        description="Discount codes, which Stripe applies at checkout, and access codes, which give an item away without Stripe."
      />

      <Banner tone="info">
        <p>{PROMO_COPY.learnersEnterInStripe}</p>
      </Banner>

      {settings.isPending && <Spinner label="Reading what this academy may issue…" />}
      {settings.isError && <Refusal error={settings.error} />}

      {/* THREE ANSWERS PER PHASE, and `unknown` is not `off`. A screen that says "switched off
          for this academy" on the strength of a failed request has told an operator something
          about their configuration that it did not read. */}
      {discountPhase === 'off' && (
        <Banner tone="warning">
          <p>
            <strong>Discount codes are not switched on for this academy.</strong> Nothing here
            can turn them on — it is a platform decision. Ask Mereka to enable them.
          </p>
        </Banner>
      )}
      {accessPhase === 'off' && (
        <Banner tone="warning">
          <p>
            <strong>Access codes are not switched on for this academy.</strong> A 100%-off
            promotion is an access code, so that is the switch it needs.
          </p>
        </Banner>
      )}
      {/* Switched on and still not usable — two true facts, both shown, in the server's words. */}
      {discountPhase === 'on' && settings.data?.stripe_ready.ready === false && (
        <Banner tone="warning">
          <p>
            <strong>Discount codes are switched on and cannot be used yet.</strong>{' '}
            {settings.data.stripe_ready.message}
          </p>
        </Banner>
      )}

      {created && (
        <Banner tone="success">
          <p>
            <strong>Created.</strong> The code is <code>{created.code}</code>. Learners type it
            into Stripe&rsquo;s field on the checkout page.
          </p>
        </Banner>
      )}

      <Card title="This academy’s codes">
        {campaigns.isPending ? (
          <Spinner label="Loading this academy’s codes…" />
        ) : campaigns.isError ? (
          <Refusal error={campaigns.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No codes yet"
            body="Nothing has been created for this academy. That is the academy’s answer, not a failure to look."
          />
        ) : (
          <DataTable
            columns={[
              ...columns(),
              // A COLUMN rather than a whole-row click. `DataTable` has no `onRowClick`, and
              // adding one would make every row in every other admin table clickable too.
              {
                key: 'open',
                header: '',
                render: (row: WireCampaign) => (
                  <Button variant="ghost" onClick={() => setChosen(row.id)}>
                    Open
                  </Button>
                ),
              },
            ]}
            rows={rows}
            rowKey={(row) => String(row.id)}
            empty={null}
          />
        )}
      </Card>

      {selected && <CampaignDetail row={selected} onClose={() => setChosen(null)} />}

      {(discountPhase === 'on' || accessPhase === 'on') && (
        <CreateCampaign
          canMintDiscount={discountPhase === 'on'}
          canMintAccess={accessPhase === 'on'}
          onCreated={(row) => {
            setCreated(row);
            void queryClient.invalidateQueries({ queryKey: PROMOTIONS_KEY });
          }}
        />
      )}
    </>
  );
}
